import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { sendEmailStream, toPdfMode, getMyEmails } from '../api/client'
import type { PdfAttachOptions, SendEmailResult, SendEmailStreamEvent, UserEmailOut } from '../api/client'
import { useStore } from '../store'

interface EmailFile {
  label: string
  filename: string | null
  docType: 'order_response' | 'despatch_advice' | 'invoice'
}

interface Props {
  sessionId: number
  files: EmailFile[]
  hasPdf: boolean
  onSent: (result: SendEmailResult) => void
  onCancel: () => void
}

type Phase = 'confirm' | 'sending' | 'done' | 'error'

interface SendStep {
  doc_type: string
  label: string
  status: 'pending' | 'sending' | 'sent'
}

const DOC_LABEL: Record<string, string> = {
  order_response: 'OrderResponse',
  despatch_advice: 'DespatchAdvice',
  invoice: 'Factuur',
}

const INLINE_NOT_SUPPORTED = new Set(['order_response'])

const PDF_KEY_MAP: Record<string, keyof Pick<PdfAttachOptions, 'ordrsp_file' | 'ordrsp_inline' | 'desadv_file' | 'desadv_inline' | 'invoice_file' | 'invoice_inline'>> = {
  order_response_file:    'ordrsp_file',
  order_response_inline:  'ordrsp_inline',
  despatch_advice_file:   'desadv_file',
  despatch_advice_inline: 'desadv_inline',
  invoice_file:           'invoice_file',
  invoice_inline:         'invoice_inline',
}

const DEFAULT_PDF_OPTS: PdfAttachOptions = {
  ordrsp_file: false, ordrsp_inline: false,
  desadv_file: false, desadv_inline: false,
  invoice_file: false, invoice_inline: false,
  report: false,
}

const LS_PDF_KEY = 'emailModal_pdfOpts'
const LS_STRIP_KEY = 'emailModal_stripPdf'
const LS_INCLUDE_KEY = 'emailModal_includeMap'

function loadPdfOpts(): PdfAttachOptions {
  try {
    const raw = localStorage.getItem(LS_PDF_KEY)
    if (!raw) return DEFAULT_PDF_OPTS
    return { ...DEFAULT_PDF_OPTS, ...JSON.parse(raw) }
  } catch {
    return DEFAULT_PDF_OPTS
  }
}

function loadStripPdf(): boolean {
  return localStorage.getItem(LS_STRIP_KEY) === 'true'
}

function loadIncludeMap(files: EmailFile[]): Record<string, boolean> {
  const defaults = Object.fromEntries(files.map(f => [f.docType, true]))
  try {
    const raw = localStorage.getItem(LS_INCLUDE_KEY)
    if (!raw) return defaults
    return { ...defaults, ...JSON.parse(raw) }
  } catch {
    return defaults
  }
}

function StepIcon({ status }: { status: SendStep['status'] }) {
  if (status === 'sent') {
    return (
      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </span>
    )
  }
  if (status === 'sending') {
    return (
      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
        <svg className="w-3.5 h-3.5 text-indigo-600 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </span>
    )
  }
  return (
    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-400 text-xs font-semibold flex items-center justify-center">
      —
    </span>
  )
}

export function EmailConfirmModal({ sessionId, files, hasPdf, onSent, onCancel }: Props) {
  const { t } = useTranslation()
  const { loadedTestCase } = useStore()
  const [includeMap, setIncludeMap] = useState<Record<string, boolean>>(
    () => loadIncludeMap(files)
  )
  const [stripPdf, setStripPdf] = useState(loadStripPdf)
  const [pdfOpts, setPdfOpts] = useState<PdfAttachOptions>(loadPdfOpts)
  const [phase, setPhase] = useState<Phase>('confirm')
  const [steps, setSteps] = useState<SendStep[]>([])
  const [sendResult, setSendResult] = useState<SendEmailResult | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [emailAddresses, setEmailAddresses] = useState<UserEmailOut[]>([])
  const [selectedEmail, setSelectedEmail] = useState<string>('')

  useEffect(() => {
    getMyEmails().then(list => {
      const approved = list.filter(e => e.status === 'approved')
      setEmailAddresses(approved)
      const primary = approved.find(e => e.is_primary) ?? approved[0]
      if (primary) setSelectedEmail(primary.email)
    }).catch(() => {})
  }, [])

  const activeFiles = files.filter(f => includeMap[f.docType])

  const toggleInclude = (docType: string) => {
    setIncludeMap(prev => {
      const next = { ...prev, [docType]: !prev[docType] }
      localStorage.setItem(LS_INCLUDE_KEY, JSON.stringify(next))
      return next
    })
  }

  const togglePdf = (key: keyof PdfAttachOptions) => {
    setPdfOpts(prev => {
      const next = { ...prev, [key]: !prev[key] }
      localStorage.setItem(LS_PDF_KEY, JSON.stringify(next))
      return next
    })
  }

  const hasPdfSelection = Object.values(pdfOpts).some(Boolean)

  const handleSend = async () => {
    const initialSteps: SendStep[] = activeFiles.map(f => ({
      doc_type: f.docType,
      label: DOC_LABEL[f.docType] ?? f.label,
      status: 'pending',
    }))
    setSteps(initialSteps)
    setPhase('sending')

    const handleEvent = (event: SendEmailStreamEvent) => {
      if (event.type === 'sending') {
        setSteps(prev => prev.map(s =>
          s.doc_type === event.doc_type ? { ...s, status: 'sending' } : s
        ))
      } else if (event.type === 'sent') {
        setSteps(prev => prev.map(s =>
          s.doc_type === event.doc_type ? { ...s, status: 'sent' } : s
        ))
      } else if (event.type === 'done') {
        const result: SendEmailResult = {
          sent: event.sent ?? 0,
          to: event.to ?? '',
          or_ref: event.or_ref ?? null,
          da_ref: event.da_ref ?? null,
          inv_ref: event.inv_ref ?? null,
        }
        setSendResult(result)
        setPhase('done')
      } else if (event.type === 'error') {
        setErrorMessage(event.detail ?? t('email_confirm_modal.unknown_error'))
        setPhase('error')
      }
    }

    try {
      await sendEmailStream(
        sessionId,
        {
          strip_pdf: stripPdf,
          pdf_ordrsp: toPdfMode(pdfOpts.ordrsp_file, pdfOpts.ordrsp_inline),
          pdf_desadv: toPdfMode(pdfOpts.desadv_file, pdfOpts.desadv_inline),
          pdf_invoice: toPdfMode(pdfOpts.invoice_file, pdfOpts.invoice_inline),
          pdf_report: pdfOpts.report,
          include_ordrsp: includeMap['order_response'] ?? false,
          include_desadv: includeMap['despatch_advice'] ?? false,
          include_invoice: includeMap['invoice'] ?? false,
          test_case_label: loadedTestCase?.testCaseName,
          scenario_name: loadedTestCase?.scenarioName,
          to_address: selectedEmail || undefined,
        },
        handleEvent,
      )
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : t('email_confirm_modal.send_failed'))
      setPhase('error')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 p-6 space-y-5 max-h-[90vh] overflow-y-auto">

        {/* ── Bevestigingsfase ── */}
        {phase === 'confirm' && (
          <>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('email_confirm_modal.title_confirm')}</h2>

            {/* E-mailadres kiezen */}
            {emailAddresses.length > 1 ? (
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('email_confirm_modal.send_to_label')}</label>
                <select
                  value={selectedEmail}
                  onChange={e => setSelectedEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {emailAddresses.map(e => (
                    <option key={e.id} value={e.email}>
                      {e.email}{e.label ? ` (${e.label})` : ''}{e.is_primary ? ' ★' : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : selectedEmail ? (
              <p className="text-xs text-gray-500">
                {t('email_confirm_modal.sending_to_prefix')} <span className="font-medium text-gray-800 dark:text-gray-200">{selectedEmail}</span>
              </p>
            ) : null}

            <p className="text-sm text-gray-500">
              {t('email_confirm_modal.select_documents_intro')}{' '}
              {activeFiles.length > 0
                ? <>(<strong>{activeFiles.length}</strong> e-mail{activeFiles.length !== 1 ? 's' : ''})</>
                : <span className="text-red-500">{t('email_confirm_modal.none_selected')}</span>}:
            </p>

            <ul className="space-y-2">
              {files.map((f) => {
                const included = includeMap[f.docType] ?? true
                return (
                  <li key={f.docType}>
                    <label className={`flex gap-3 items-start cursor-pointer select-none rounded-lg px-2 py-1.5 transition-colors ${included ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'bg-gray-50 dark:bg-gray-900 opacity-50'}`}>
                      <input
                        type="checkbox"
                        checked={included}
                        onChange={() => toggleInclude(f.docType)}
                        className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{f.label}</div>
                        {f.filename && (
                          <div className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate" title={f.filename}>
                            {f.filename}
                          </div>
                        )}
                      </div>
                    </label>
                  </li>
                )
              })}
            </ul>

            {/* PDF-bijlagen sectie */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{t('email_confirm_modal.pdf_attachments_title')}</p>
              <p className="text-xs text-gray-500">
                {t('email_confirm_modal.pdf_attachments_description_prefix')} <span className="font-medium">{t('email_confirm_modal.pdf_loose_attachment')}</span> {t('email_confirm_modal.pdf_attachments_description_or')} <span className="font-medium">{t('email_confirm_modal.pdf_embedded_in_xml')}</span> {t('email_confirm_modal.pdf_attachments_description_suffix')}
              </p>
              <div className="space-y-2">
                {files.filter(f => includeMap[f.docType]).map((f) => {
                  const fileKey = PDF_KEY_MAP[`${f.docType}_file`]
                  const inlineKey = PDF_KEY_MAP[`${f.docType}_inline`]
                  const label = DOC_LABEL[f.docType] ?? f.label
                  return (
                    <div key={f.docType} className="flex items-center gap-4 py-1.5 px-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 w-28 flex-shrink-0">{label}</span>
                      <label className="flex items-center gap-1.5 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={pdfOpts[fileKey]}
                          onChange={() => togglePdf(fileKey)}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-xs text-gray-700 dark:text-gray-300">{t('email_confirm_modal.pdf_option_loose')}</span>
                      </label>
                      {INLINE_NOT_SUPPORTED.has(f.docType) ? (
                        <span
                          className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 cursor-not-allowed"
                          title={t('email_confirm_modal.inline_not_supported_tooltip')}
                        >
                          <input type="checkbox" disabled className="w-4 h-4 rounded border-gray-200 opacity-40" />
                          {t('email_confirm_modal.pdf_option_inline')}
                        </span>
                      ) : (
                        <label className="flex items-center gap-1.5 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={pdfOpts[inlineKey]}
                            onChange={() => togglePdf(inlineKey)}
                            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-xs text-gray-700 dark:text-gray-300">{t('email_confirm_modal.pdf_option_inline')}</span>
                        </label>
                      )}
                    </div>
                  )
                })}
                <label className="flex items-center gap-2.5 cursor-pointer select-none py-1.5 px-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
                  <input
                    type="checkbox"
                    checked={pdfOpts.report}
                    onChange={() => togglePdf('report')}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">
                    <span className="font-medium">{t('email_confirm_modal.reconciliation_report_label')}</span> {t('email_confirm_modal.reconciliation_report_suffix')}{' '}
                    <span className="text-gray-400 dark:text-gray-500">{t('email_confirm_modal.reconciliation_report_note')}</span>
                  </span>
                </label>
              </div>
            </div>

            {hasPdf && (
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={stripPdf}
                  onChange={e => {
                    setStripPdf(e.target.checked)
                    localStorage.setItem(LS_STRIP_KEY, String(e.target.checked))
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {t('email_confirm_modal.strip_existing_pdf_label')}
                </span>
              </label>
            )}

            <p className="text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/50 rounded-lg px-3 py-2">
              {t('email_confirm_modal.reference_numbers_info')}
            </p>

            {hasPdfSelection && (
              <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/50 rounded-lg px-3 py-2">
                {t('email_confirm_modal.pdf_generation_info')}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('email_confirm_modal.button_cancel')}
              </button>
              <button
                onClick={handleSend}
                disabled={activeFiles.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t('email_confirm_modal.button_send')}
              </button>
            </div>
          </>
        )}

        {/* ── Verzendfase ── */}
        {phase === 'sending' && (
          <>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('email_confirm_modal.title_sending')}</h2>
            <ul className="space-y-3">
              {steps.map((step, i) => (
                <li key={i} className="flex items-center gap-3">
                  <StepIcon status={step.status} />
                  <div>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{step.label}</span>
                    {step.status === 'sending' && (
                      <span className="ml-2 text-xs text-indigo-600">{t('email_confirm_modal.step_status_sending')}</span>
                    )}
                    {step.status === 'sent' && (
                      <span className="ml-2 text-xs text-green-600">{t('email_confirm_modal.step_status_sent')}</span>
                    )}
                    {step.status === 'pending' && (
                      <span className="ml-2 text-xs text-gray-400">{t('email_confirm_modal.step_status_pending')}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* ── Succesfase ── */}
        {phase === 'done' && sendResult && (
          <>
            <div className="flex items-center gap-3">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {sendResult.sent} e-mail{sendResult.sent !== 1 ? 's' : ''} {t('email_confirm_modal.title_done_suffix')}
              </h2>
            </div>
            <p className="text-sm text-gray-500">{t('email_confirm_modal.recipient_label')} <span className="font-medium text-gray-800 dark:text-gray-200">{sendResult.to}</span></p>
            <ul className="space-y-2">
              {steps.map((step, i) => (
                <li key={i} className="flex items-center gap-3">
                  <StepIcon status="sent" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{step.label}</span>
                </li>
              ))}
            </ul>
            {(sendResult.or_ref || sendResult.da_ref || sendResult.inv_ref) && (
              <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-lg px-3 py-2 space-y-0.5">
                {sendResult.or_ref && <div>{t('email_confirm_modal.or_reference_label')} <span className="font-mono font-medium text-gray-800 dark:text-gray-200">{sendResult.or_ref}</span></div>}
                {sendResult.da_ref && <div>{t('email_confirm_modal.da_reference_label')} <span className="font-mono font-medium text-gray-800 dark:text-gray-200">{sendResult.da_ref}</span></div>}
                {sendResult.inv_ref && <div>{t('email_confirm_modal.inv_reference_label')} <span className="font-mono font-medium text-gray-800 dark:text-gray-200">{sendResult.inv_ref}</span></div>}
              </div>
            )}
            <div className="flex justify-end pt-1">
              <button
                onClick={() => onSent(sendResult)}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                {t('email_confirm_modal.button_close')}
              </button>
            </div>
          </>
        )}

        {/* ── Foutfase ── */}
        {phase === 'error' && (
          <>
            <div className="flex items-center gap-3">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </span>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('email_confirm_modal.title_error')}</h2>
            </div>
            <p className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 rounded-lg px-3 py-2">
              {errorMessage}
            </p>
            <div className="flex justify-end pt-1">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('email_confirm_modal.button_close')}
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  )
}
